import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Users,
  Target,
  Mail,
  Phone,
  Globe,
  DollarSign,
  Zap,
  CheckCircle,
  Loader2,
  TrendingUp,
  BarChart3,
  Megaphone,
  UserPlus,
  List,
  ShoppingCart
} from "lucide-react";

const acquireOptions = [
  {
    id: "create-ad",
    title: "Create Ad",
    description: "Design and deploy targeted ads to reach new customers",
    icon: Megaphone,
    color: "from-primary to-purple-600",
    features: ["AI-powered ad copy", "Multi-platform deployment", "A/B testing", "Performance tracking"]
  },
  {
    id: "get-lists",
    title: "Get Customer Lists",
    description: "Access targeted prospect lists based on your ideal customer profile",
    icon: List,
    color: "from-accent-neon to-blue-600",
    features: ["Industry targeting", "Location filtering", "Company size", "Verified contacts"]
  },
  {
    id: "acquire-buyer",
    title: "Acquire Buyer",
    description: "Convert prospects into paying customers with automated outreach",
    icon: UserPlus,
    color: "from-accent-success to-emerald-600",
    features: ["Smart follow-ups", "Multi-channel outreach", "Lead scoring", "Conversion tracking"]
  }
];

const listSources = [
  { name: "Hunter.io", icon: "🎯", type: "Email Discovery" },
  { name: "Apollo.io", icon: "🚀", type: "B2B Leads" },
  { name: "LinkedIn", icon: "💼", type: "Professional" },
  { name: "ZoomInfo", icon: "🔍", type: "Company Data" },
  { name: "Clearbit", icon: "✨", type: "Enrichment" },
  { name: "Custom Import", icon: "📁", type: "Your Lists" }
];

const adPlatforms = [
  { name: "Google Ads", icon: "🔎" },
  { name: "Facebook", icon: "👤" },
  { name: "Instagram", icon: "📷" },
  { name: "TikTok", icon: "🎵" },
  { name: "LinkedIn", icon: "💼" },
  { name: "YouTube", icon: "📺" },
  { name: "Trade Desk", icon: "📡" },
  { name: "DV360", icon: "🎬" }
];

export default function AcquireCustomers() {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [adCopy, setAdCopy] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [budget, setBudget] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const toggleSource = (source) => {
    setSelectedSources(prev => 
      prev.includes(source) 
        ? prev.filter(s => s !== source)
        : [...prev, source]
    );
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    if (selectedOption === 'create-ad') {
      toast.success("Ad campaign created! Deploying to selected platforms...");
    } else if (selectedOption === 'get-lists') {
      toast.success("Generating customer list... You'll receive it shortly.");
    } else {
      toast.success("Buyer acquisition campaign started!");
    }
    
    setLoading(false);
    
    // Navigate to broadcast to continue
    setTimeout(() => navigate("/broadcast"), 1000);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-text-secondary hover:text-text-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <span className="font-heading font-bold text-text-primary">Acquire Customers</span>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-8">
          
          {/* Hero */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-4">
                <TrendingUp className="w-4 h-4 text-primary" />
                <span className="text-primary text-sm font-medium">Grow Your Business</span>
              </div>
              <h1 className="font-heading font-bold text-3xl text-text-primary mb-2">
                Create Ad • Get Lists • Acquire Buyers
              </h1>
              <p className="text-text-secondary max-w-xl mx-auto">
                Everything you need to find and convert new customers. 
                Create targeted ads, access prospect lists, and automate your outreach.
              </p>
            </motion.div>
          </div>

          {/* Option Selection */}
          {!selectedOption ? (
            <div className="grid md:grid-cols-3 gap-4">
              {acquireOptions.map((option, index) => {
                const IconComponent = option.icon;
                return (
                  <motion.div
                    key={option.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card 
                      className="h-full bg-background-paper border-border hover:border-primary/50 transition-all cursor-pointer group"
                      onClick={() => setSelectedOption(option.id)}
                    >
                      <CardContent className="p-6">
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${option.color} flex items-center justify-center text-white mb-4`}>
                          <IconComponent className="w-7 h-7" />
                        </div>
                        <h3 className="font-heading font-bold text-lg text-text-primary mb-2 group-hover:text-primary transition-colors">
                          {option.title}
                        </h3>
                        <p className="text-text-secondary text-sm mb-4">
                          {option.description}
                        </p>
                        <ul className="space-y-2">
                          {option.features.map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-xs text-text-muted">
                              <CheckCircle className="w-3 h-3 text-accent-success" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        <Button className="w-full mt-4 bg-background-subtle hover:bg-primary hover:text-white text-text-primary group-hover:bg-primary group-hover:text-white">
                          Get Started
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {/* Back to options */}
              <Button 
                variant="ghost" 
                onClick={() => setSelectedOption(null)}
                className="mb-6 text-text-secondary"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to options
              </Button>

              {/* Create Ad Form */}
              {selectedOption === 'create-ad' && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6 space-y-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white">
                        <Megaphone className="w-6 h-6" />
                      </div>
                      <div>
                        <h2 className="font-heading font-bold text-xl text-text-primary">Create Ad Campaign</h2>
                        <p className="text-sm text-text-muted">Design and deploy targeted ads</p>
                      </div>
                    </div>

                    <div>
                      <Label className="text-text-primary mb-2 block">Ad Copy / Message</Label>
                      <Textarea
                        value={adCopy}
                        onChange={(e) => setAdCopy(e.target.value)}
                        placeholder="Write your ad copy or let AI generate it for you..."
                        className="bg-background border-border text-text-primary min-h-[100px]"
                      />
                      <Button variant="ghost" size="sm" className="mt-2 text-primary">
                        <Zap className="w-4 h-4 mr-1" />
                        Generate with AI
                      </Button>
                    </div>

                    <div>
                      <Label className="text-text-primary mb-2 block">Target Audience</Label>
                      <Input
                        value={targetAudience}
                        onChange={(e) => setTargetAudience(e.target.value)}
                        placeholder="e.g., Small business owners, 25-45, interested in luxury watches"
                        className="bg-background border-border text-text-primary"
                      />
                    </div>

                    <div>
                      <Label className="text-text-primary mb-2 block">Daily Budget</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                        <Input
                          value={budget}
                          onChange={(e) => setBudget(e.target.value)}
                          placeholder="50"
                          className="bg-background border-border text-text-primary pl-9"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-text-primary mb-3 block">Select Platforms</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {adPlatforms.map(platform => (
                          <button
                            key={platform.name}
                            onClick={() => togglePlatform(platform.name)}
                            className={`p-3 rounded-lg border text-center transition-all ${
                              selectedPlatforms.includes(platform.name)
                                ? 'border-primary bg-primary/10'
                                : 'border-border bg-background hover:border-primary/50'
                            }`}
                          >
                            <span className="text-xl block mb-1">{platform.icon}</span>
                            <span className="text-xs text-text-secondary">{platform.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <Button 
                      onClick={handleSubmit}
                      disabled={loading}
                      className="w-full bg-primary hover:bg-primary-hover text-white py-6"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <Megaphone className="w-5 h-5 mr-2" />
                      )}
                      Launch Ad Campaign
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Get Lists Form */}
              {selectedOption === 'get-lists' && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6 space-y-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-neon to-blue-600 flex items-center justify-center text-white">
                        <List className="w-6 h-6" />
                      </div>
                      <div>
                        <h2 className="font-heading font-bold text-xl text-text-primary">Get Customer Lists</h2>
                        <p className="text-sm text-text-muted">Access targeted prospect lists</p>
                      </div>
                    </div>

                    <div>
                      <Label className="text-text-primary mb-2 block">Ideal Customer Profile</Label>
                      <Textarea
                        value={targetAudience}
                        onChange={(e) => setTargetAudience(e.target.value)}
                        placeholder="Describe your ideal customer: industry, company size, job title, location..."
                        className="bg-background border-border text-text-primary min-h-[100px]"
                      />
                    </div>

                    <div>
                      <Label className="text-text-primary mb-3 block">Data Sources</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {listSources.map(source => (
                          <button
                            key={source.name}
                            onClick={() => toggleSource(source.name)}
                            className={`p-4 rounded-lg border text-center transition-all ${
                              selectedSources.includes(source.name)
                                ? 'border-accent-neon bg-accent-neon/10'
                                : 'border-border bg-background hover:border-accent-neon/50'
                            }`}
                          >
                            <span className="text-2xl block mb-1">{source.icon}</span>
                            <span className="text-sm text-text-primary font-medium block">{source.name}</span>
                            <span className="text-xs text-text-muted">{source.type}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-text-primary mb-2 block">List Size</Label>
                        <select className="w-full p-3 rounded-lg bg-background border border-border text-text-primary">
                          <option>100 contacts</option>
                          <option>500 contacts</option>
                          <option>1,000 contacts</option>
                          <option>5,000 contacts</option>
                          <option>10,000 contacts</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-text-primary mb-2 block">Contact Type</Label>
                        <select className="w-full p-3 rounded-lg bg-background border border-border text-text-primary">
                          <option>Email only</option>
                          <option>Email + Phone</option>
                          <option>Email + Phone + LinkedIn</option>
                        </select>
                      </div>
                    </div>

                    <Button 
                      onClick={handleSubmit}
                      disabled={loading}
                      className="w-full bg-accent-neon hover:bg-accent-neon/90 text-white py-6"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <List className="w-5 h-5 mr-2" />
                      )}
                      Generate Customer List
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Acquire Buyer Form */}
              {selectedOption === 'acquire-buyer' && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6 space-y-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-success to-emerald-600 flex items-center justify-center text-white">
                        <UserPlus className="w-6 h-6" />
                      </div>
                      <div>
                        <h2 className="font-heading font-bold text-xl text-text-primary">Acquire Buyers</h2>
                        <p className="text-sm text-text-muted">Convert prospects into customers</p>
                      </div>
                    </div>

                    <div className="bg-accent-success/10 border border-accent-success/30 rounded-lg p-4">
                      <p className="text-sm text-text-secondary">
                        <strong className="text-accent-success">Smart Acquisition</strong> combines your customer lists 
                        with automated multi-channel outreach to convert prospects into paying customers.
                      </p>
                    </div>

                    <div>
                      <Label className="text-text-primary mb-2 block">Your Offer</Label>
                      <Textarea
                        value={adCopy}
                        onChange={(e) => setAdCopy(e.target.value)}
                        placeholder="What are you offering? Describe your product/service and any special offers..."
                        className="bg-background border-border text-text-primary min-h-[100px]"
                      />
                    </div>

                    <div>
                      <Label className="text-text-primary mb-3 block">Outreach Channels</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { name: "Email", icon: <Mail className="w-5 h-5" /> },
                          { name: "SMS", icon: <Phone className="w-5 h-5" /> },
                          { name: "WhatsApp", icon: "💬" },
                          { name: "LinkedIn", icon: "💼" }
                        ].map(channel => (
                          <button
                            key={channel.name}
                            onClick={() => togglePlatform(channel.name)}
                            className={`p-4 rounded-lg border text-center transition-all ${
                              selectedPlatforms.includes(channel.name)
                                ? 'border-accent-success bg-accent-success/10'
                                : 'border-border bg-background hover:border-accent-success/50'
                            }`}
                          >
                            <span className="text-xl block mb-1">
                              {typeof channel.icon === 'string' ? channel.icon : channel.icon}
                            </span>
                            <span className="text-sm text-text-secondary">{channel.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-text-primary mb-2 block">Follow-up Sequence</Label>
                        <select className="w-full p-3 rounded-lg bg-background border border-border text-text-primary">
                          <option>3 touches over 7 days</option>
                          <option>5 touches over 14 days</option>
                          <option>7 touches over 21 days</option>
                          <option>Custom sequence</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-text-primary mb-2 block">Daily Limit</Label>
                        <select className="w-full p-3 rounded-lg bg-background border border-border text-text-primary">
                          <option>50 contacts/day</option>
                          <option>100 contacts/day</option>
                          <option>200 contacts/day</option>
                          <option>500 contacts/day</option>
                        </select>
                      </div>
                    </div>

                    <Button 
                      onClick={handleSubmit}
                      disabled={loading}
                      className="w-full bg-accent-success hover:bg-accent-success/90 text-white py-6"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <ShoppingCart className="w-5 h-5 mr-2" />
                      )}
                      Start Acquiring Buyers
                    </Button>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {/* Stats */}
          {!selectedOption && (
            <div className="grid grid-cols-3 gap-4 mt-8">
              {[
                { value: "10K+", label: "Leads Generated", icon: Users },
                { value: "85%", label: "Open Rate", icon: Mail },
                { value: "3.2x", label: "ROI Average", icon: BarChart3 }
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="text-center p-4 rounded-xl bg-background-paper border border-border"
                >
                  <stat.icon className="w-6 h-6 text-primary mx-auto mb-2" />
                  <div className="font-heading font-bold text-2xl text-text-primary">{stat.value}</div>
                  <div className="text-xs text-text-muted">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
